"use client";

import React, { useEffect, useRef, useState } from "react";
import { Tooltip, TooltipFocusableTrigger } from "@zapier/design-system";

import styles from "./TooltipText.module.css";

interface Props {
  children: React.ReactNode;
  id: string;
  label: string;
  placement: "top" | "bottom" | "left" | "right";
  shouldNotBeFocusable?: boolean;
}

const TooltipText = ({
  children,
  id,
  label,
  placement,
  shouldNotBeFocusable = false,
}: Props) => {
  const textReference = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = () => {
    if (textReference.current) {
      const clientWidth = textReference.current.clientWidth;
      const scrollWidth = textReference.current.scrollWidth;

      // Check if scrollWidth exceeds clientWidth (indicating truncation)
      setIsTruncated(scrollWidth > clientWidth);
    }
  };

  useEffect(() => {
    // Run the truncation check when the component mounts or the text changes
    checkTruncation();
  }, [id]); // Effect runs when 'text' changes

  return (
    <>
      {isTruncated ? (
        <div className={styles.ellipsisText}>
          <Tooltip
            allowMultiline={true}
            content={label}
            key={id}
            placement={placement}
          >
            {shouldNotBeFocusable ? (
              <>{children}</>
            ) : (
              <TooltipFocusableTrigger>{children}</TooltipFocusableTrigger>
            )}
          </Tooltip>
        </div>
      ) : (
        <span ref={textReference} className={styles.ellipsisText}>
          {children}
        </span>
      )}
    </>
  );
};

export default TooltipText;
